import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    ASSIGNEE_ROLE_LABEL,
    ASSIGNEE_ROLE_OPTIONS,
    NO_ASSIGNEE_ROLE_VALUE,
    assigneeRoleLabel,
} from "../closeTemplateTasksDrawer.constants";
import {
    CloseTemplateTaskCallbacks,
    CloseTemplateTaskRow,
} from "../closeTemplateTasksViewConfig";

type Meta = ViewTableMeta<CloseTemplateTaskCallbacks, never>;

// Built on first render, not at module load: the constants module imports
// this cell through `closeTemplateTasksViewConfig`, so its exports are not
// initialized yet when this module evaluates.
const buildAssigneeRoleItems = (): Array<
    PickerItemData<TaskTemplateAssigneeRole | typeof NO_ASSIGNEE_ROLE_VALUE>
> => [
    { value: NO_ASSIGNEE_ROLE_VALUE, label: assigneeRoleLabel(null) },
    ...ASSIGNEE_ROLE_OPTIONS.map((role) => ({
        value: role,
        label: ASSIGNEE_ROLE_LABEL[role],
    })),
];

export const CloseTemplateTaskAssigneeCell = memo(
    function CloseTemplateTaskAssigneeCell({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        CloseTemplateTaskRow,
        TaskTemplateAssigneeRole | null
    >) {
        const meta = (table.options.meta ?? {}) as Meta;
        const item = row.original;
        const assigneeRoleItems = useMemo(() => buildAssigneeRoleItems(), []);

        if (item.isGroupRow) {
            return null;
        }

        return (
            <ViewTableCell>
                <Picker.Select
                    trigger="cell"
                    items={assigneeRoleItems}
                    value={item.assigneeRole ?? NO_ASSIGNEE_ROLE_VALUE}
                    onValueChange={(value) => {
                        if (!value) {
                            return;
                        }
                        meta.callbacks?.onUpdateAssigneeRole(
                            item.id,
                            value === NO_ASSIGNEE_ROLE_VALUE ? null : value,
                        );
                    }}
                />
            </ViewTableCell>
        );
    },
);
